import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Bell, Paperclip, Download, X, ChevronDown, ChevronRight, ArrowUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ExportButtons from "@/components/ExportButtons";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const statusBadge = { active: "bg-emerald-100 text-emerald-700 border-emerald-200", expiring_soon: "bg-amber-100 text-amber-700 border-amber-200", expired: "bg-red-100 text-red-700 border-red-200" };
const statusLabel = { active: "Active", expiring_soon: "Expiring Soon", expired: "Expired" };

export default function CertificatesPage() {
  useDocumentTitle("Certificates", "Issued certificates with expiry tracking and reminders.");
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [qualFilter, setQualFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: /certificates?open=<memberId> auto-expands that learner's group.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setExpanded(prev => ({ ...prev, [openId]: true }));
      const params = new URLSearchParams(searchParams);
      params.delete("open");
      setSearchParams(params, { replace: true });
      // Scroll into view once the row is mounted
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="cert-group-row-${openId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [searchParams, setSearchParams]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [members, setMembers] = useState([]);
  const [quals, setQuals] = useState([]);
  const [form, setForm] = useState({ member_id: "", qualification_id: "", certificate_number: "", issue_date: "", expiry_date: "", certification_fee: 0, notes: "" });
  const [qualSearch, setQualSearch] = useState("");
  const [selectedQuals, setSelectedQuals] = useState([]); // for bulk create: array of qualification_id
  const [attachment, setAttachment] = useState(null); // File object for new upload
  const fileInputRef = useRef(null);
  const [expandedCerts, setExpandedCerts] = useState(new Set());

  const fetchCerts = useCallback(async () => {
    try {
      // Request a generous cap — backend defaults to 100 which was truncating
      // recently-imported certs on any decent-sized customer.  We paginate
      // client-side; 10 k is more than enough for any real org, and the
      // response payload stays small because achievements are lightweight.
      const qp = new URLSearchParams();
      qp.set("limit", "10000");
      if (statusFilter) qp.set("status", statusFilter);
      const { data } = await api.get(`/certificates?${qp.toString()}`);
      setCerts(data.certificates); setTotal(data.total);
    } catch (err) { if (!isAuthError(err)) toast.error("Failed to load certificates"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  const loadFormData = async () => {
    // Same "500 truncates for large orgs" fix as the enrollment dropdown.
    const [m, q] = await Promise.all([api.get("/members?limit=10000&role=student"), api.get("/qualifications")]);
    setMembers(m.data.members); setQuals(q.data);
  };

  const openCreate = async () => {
    setEditing(null);
    setForm({ member_id: "", qualification_id: "", certificate_number: "", issue_date: "", expiry_date: "", certification_fee: 0, notes: "" });
    setSelectedQuals([]);
    setQualSearch("");
    setAttachment(null);
    await loadFormData(); setDialogOpen(true);
  };
  const openEdit = async (c) => {
    setEditing(c); setForm({ member_id: c.member_id, qualification_id: c.qualification_id, certificate_number: c.certificate_number || "", issue_date: c.issue_date, expiry_date: c.expiry_date, certification_fee: c.certification_fee || 0, notes: c.notes || "" });
    setAttachment(null);
    await loadFormData(); setDialogOpen(true);
  };

  // Auto-calculate expiry date as issue_date + qualification.certificate_validity_years
  // unless the user has manually changed it.
  const computeExpiry = (issueDate, qualId) => {
    if (!issueDate || !qualId) return "";
    const q = quals.find(x => x.id === qualId);
    if (!q || !q.certificate_validity_years) return "";
    const d = new Date(issueDate);
    if (Number.isNaN(d.getTime())) return "";
    d.setFullYear(d.getFullYear() + q.certificate_validity_years);
    return d.toISOString().slice(0, 10);
  };
  const onIssueDateChange = (v) => {
    const auto = computeExpiry(v, form.qualification_id);
    setForm(f => ({ ...f, issue_date: v, ...(auto && !editing ? { expiry_date: auto } : {}) }));
  };
  const uploadAttachment = async (certId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/certificates/${certId}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const downloadAttachment = async (c) => {
    try {
      const r = await api.get(`/certificates/${c.id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = c.attachment_filename || "attachment"; a.target = "_blank";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error("Download failed"); }
  };

  const removeAttachment = async (c) => {
    if (!window.confirm(`Remove attachment from this certificate?`)) return;
    try {
      await api.delete(`/certificates/${c.id}/attachment`);
      toast.success("Attachment removed");
      fetchCerts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // EDIT path (single cert) — unchanged
    if (editing) {
      try {
        const { member_id: _mid, qualification_id: _qid, ...updateData } = form;
        await api.put(`/certificates/${editing.id}`, updateData);
        if (attachment) {
          try {
            await uploadAttachment(editing.id, attachment);
            toast.success("Certificate updated · attachment uploaded");
          } catch (err) {
            toast.error(`Attachment upload failed: ${formatApiError(err.response?.data?.detail) || err.message}`);
          }
        } else {
          toast.success("Certificate updated");
        }
        setDialogOpen(false); setAttachment(null); fetchCerts();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      return;
    }
    // CREATE path — bulk over selectedQuals
    if (!form.member_id) { toast.error("Please select a member"); return; }
    if (selectedQuals.length === 0) { toast.error("Please select at least one qualification"); return; }
    if (!form.issue_date) { toast.error("Please set the issue date"); return; }
    let okCount = 0; let firstId = null; let attachmentOk = false; const failures = [];
    for (const qid of selectedQuals) {
      const q = quals.find(x => x.id === qid);
      const expiry = computeExpiry(form.issue_date, qid);
      if (!expiry) { failures.push(`${q?.name || qid}: qualification has no validity years — set one on the qualification`); continue; }
      const fee = (q?.default_certification_fee ?? form.certification_fee) || 0;
      try {
        const { data } = await api.post("/certificates", {
          member_id: form.member_id,
          qualification_id: qid,
          certificate_number: form.certificate_number || "",
          issue_date: form.issue_date,
          expiry_date: expiry,
          certification_fee: fee,
          notes: form.notes || "",
        });
        if (!firstId) firstId = data.id;
        okCount++;
      } catch (err) {
        failures.push(`${q?.name || qid}: ${formatApiError(err.response?.data?.detail) || "failed"}`);
      }
    }
    if (attachment && firstId) {
      try { await uploadAttachment(firstId, attachment); attachmentOk = true; }
      catch (err) { failures.push(`Attachment: ${formatApiError(err.response?.data?.detail) || err.message}`); }
    }
    if (okCount) toast.success(`${okCount} certificate${okCount === 1 ? "" : "s"} created${attachmentOk ? " · attachment saved on the first" : ""}`);
    if (failures.length) failures.forEach(f => toast.error(f));
    if (okCount) {
      setDialogOpen(false); setAttachment(null); setSelectedQuals([]); fetchCerts();
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete certificate ${c.certificate_number || c.id}?`)) return;
    try { await api.delete(`/certificates/${c.id}`); toast.success("Certificate deleted"); fetchCerts(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendReminder = async (c) => {
    try {
      const { data } = await api.post(`/certificates/${c.id}/send-reminder`);
      if (data.email?.status === "failed") toast.error(`Email failed: ${data.email?.error || "Unknown error"}`);
      else toast.success("Reminder email sent");
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isStaff = user?.role === "admin" || user?.role === "staff" || user?.role === "superadmin";

  // ---- Filter + group + sort (client-side) ----
  const uniqueQuals = Array.from(
    new Map((certs || []).map(c => [c.qualification_id, c.qualification_name])).entries()
  ).map(([id, name]) => ({ id, name })).filter(q => q.id && q.name).sort((a, b) => a.name.localeCompare(b.name));

  const filteredCerts = qualFilter ? certs.filter(c => c.qualification_id === qualFilter) : certs;

  const groups = (() => {
    const map = new Map();
    for (const c of filteredCerts) {
      const key = c.member_id || `__nomember_${c.member_name || "Unknown"}`;
      if (!map.has(key)) {
        map.set(key, { member_id: c.member_id, member_name: c.member_name || "—", certs: [] });
      }
      map.get(key).certs.push(c);
    }
    const out = Array.from(map.values()).map(g => {
      // per-group stats
      const stats = { active: 0, expiring_soon: 0, expired: 0 };
      let earliestExpiry = null;
      for (const c of g.certs) {
        if (stats[c.computed_status] !== undefined) stats[c.computed_status]++;
        if (c.expiry_date && (!earliestExpiry || c.expiry_date < earliestExpiry)) earliestExpiry = c.expiry_date;
      }
      return { ...g, count: g.certs.length, stats, earliestExpiry };
    });
    const sorters = {
      name_asc: (a, b) => a.member_name.localeCompare(b.member_name),
      name_desc: (a, b) => b.member_name.localeCompare(a.member_name),
      certs_desc: (a, b) => b.count - a.count || a.member_name.localeCompare(b.member_name),
      expiry_asc: (a, b) => (a.earliestExpiry || "9999") .localeCompare(b.earliestExpiry || "9999"),
      expiry_desc: (a, b) => (b.earliestExpiry || "0").localeCompare(a.earliestExpiry || "0"),
    };
    out.sort(sorters[sortBy] || sorters.name_asc);
    // sort inner certs by expiry ascending for consistency
    out.forEach(g => g.certs.sort((a, b) => (a.expiry_date || "").localeCompare(b.expiry_date || "")));
    return out;
  })();

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  const expandAll = () => {
    const all = {};
    groups.forEach(g => { all[g.member_id || g.member_name] = true; });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  return (
    <div className="space-y-4" data-testid="certificates-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Tabs value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <TabsList className="h-8" data-testid="cert-status-tabs">
            <TabsTrigger value="all" className="text-xs px-3 py-1">All</TabsTrigger>
            <TabsTrigger value="active" className="text-xs px-3 py-1">Active</TabsTrigger>
            <TabsTrigger value="expiring_soon" className="text-xs px-3 py-1">Expiring</TabsTrigger>
            <TabsTrigger value="expired" className="text-xs px-3 py-1">Expired</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <ExportButtons endpoint="certificates" label="Certificates" />
          {isStaff && (
            <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-certificate-btn">
              <Plus size={16} className="mr-1" /> Add Certificate
            </Button>
          )}
        </div>
      </div>

      {/* Sort + Filter toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 bg-slate-50 border border-border rounded-md px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <ArrowUpDown size={14} className="text-slate-500 shrink-0" />
          <Label className="text-xs text-slate-600 shrink-0">Sort</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 text-xs w-[180px]" data-testid="cert-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Learner A → Z</SelectItem>
              <SelectItem value="name_desc">Learner Z → A</SelectItem>
              <SelectItem value="certs_desc">Most certificates</SelectItem>
              <SelectItem value="expiry_asc">Soonest expiry</SelectItem>
              <SelectItem value="expiry_desc">Latest expiry</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Label className="text-xs text-slate-600 shrink-0">Qualification</Label>
          <Select value={qualFilter || "__all"} onValueChange={(v) => setQualFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="cert-qual-filter"><SelectValue placeholder="All qualifications" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All qualifications</SelectItem>
              {uniqueQuals.map(q => (
                <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 md:ml-auto">
          <button onClick={expandAll} className="text-xs text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="expand-all-btn">Expand all</button>
          <span className="text-slate-300">·</span>
          <button onClick={collapseAll} className="text-xs text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="collapse-all-btn">Collapse all</button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {groups.length} learner{groups.length !== 1 ? "s" : ""} · {filteredCerts.length} certificate{filteredCerts.length !== 1 ? "s" : ""}
        {qualFilter && ` (filtered from ${total})`}
      </p>

      <div className="border border-border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="certificates-table">
          <thead><tr className="border-b border-border text-left bg-slate-50/50">
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs w-8"></th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Learner</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Certificates</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Status</th>
            <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Next Expiry</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No certificates found</td></tr>
            ) : groups.map((g, i) => {
              const key = g.member_id || g.member_name;
              const isOpen = !!expanded[key];
              return (
                <Fragment key={key}>
                  <tr
                    key={`grp-${key}`}
                    className="border-b border-border hover:bg-slate-50/60 cursor-pointer table-row-animate"
                    style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }}
                    onClick={() => toggle(key)}
                    data-testid={`cert-group-row-${key}`}
                  >
                    <td className="px-4 py-2.5 text-slate-400">
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </td>
                    <td className="px-4 py-2.5 font-medium">{g.member_name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      <span className="font-medium text-foreground">{g.count}</span> certificate{g.count !== 1 ? "s" : ""}
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <div className="flex items-center gap-1.5">
                        {g.stats.active > 0 && <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">{g.stats.active} active</Badge>}
                        {g.stats.expiring_soon > 0 && <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">{g.stats.expiring_soon} expiring</Badge>}
                        {g.stats.expired > 0 && <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-200">{g.stats.expired} expired</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{g.earliestExpiry || "—"}</td>
                  </tr>
                  {isOpen && (
                    <tr key={`sub-${key}`} className="border-b border-border bg-slate-50/40">
                      <td colSpan={5} className="px-4 py-2">
                        <div className="rounded border border-border bg-white overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`cert-sub-table-${key}`}>
                            <thead><tr className="text-left border-b border-border">
                              <th className="px-3 py-2 font-medium text-muted-foreground text-[11px]">Qualification</th>
                              <th className="px-3 py-2 font-medium text-muted-foreground text-[11px] hidden md:table-cell">Cert No.</th>
                              <th className="px-3 py-2 font-medium text-muted-foreground text-[11px] hidden lg:table-cell">Issue Date</th>
                              <th className="px-3 py-2 font-medium text-muted-foreground text-[11px]">Expiry Date</th>
                              <th className="px-3 py-2 font-medium text-muted-foreground text-[11px]">Status</th>
                              {isStaff && <th className="px-3 py-2 font-medium text-muted-foreground text-[11px] w-28">Actions</th>}
                            </tr></thead>
                            <tbody>
                              {g.certs.map(c => {
                                const hasAchievements = Array.isArray(c.achievements) && c.achievements.length > 1;
                                const isOpen = expandedCerts.has(c.id);
                                return (
                                <Fragment key={c.id}>
                                <tr className="border-b border-border/60 last:border-b-0 hover:bg-slate-50/50">
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-1.5">
                                      {hasAchievements && (
                                        <button
                                          className="text-slate-500 hover:text-slate-900"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedCerts((prev) => {
                                              const n = new Set(prev);
                                              if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                                              return n;
                                            });
                                          }}
                                          title={isOpen ? "Hide achievements" : `Show ${c.achievements.length} achievements`}
                                          data-testid={`toggle-achievements-${c.id}`}
                                        >
                                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </button>
                                      )}
                                      <div>
                                        <span>{c.qualification_name}</span>
                                        {hasAchievements && (
                                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                            +{c.achievements.length - 1} more
                                          </span>
                                        )}
                                        <span className="text-muted-foreground text-xs block">{c.qualification_category}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{c.certificate_number || "—"}</td>
                                  <td className="px-3 py-2 text-muted-foreground hidden lg:table-cell">{c.issue_date}</td>
                                  <td className="px-3 py-2">{c.expiry_date}</td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-1">
                                      <Badge variant="outline" className={`text-[10px] ${statusBadge[c.computed_status] || ""}`}>{statusLabel[c.computed_status] || c.computed_status}</Badge>
                                      {c.attachment_path && (
                                        <button onClick={(e) => { e.stopPropagation(); downloadAttachment(c); }} className="text-sky-600 hover:text-sky-700" title={`Download ${c.attachment_filename || "attachment"}`} data-testid={`cert-attachment-${c.id}`}>
                                          <Paperclip size={12} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  {isStaff && (
                                    <td className="px-3 py-2">
                                      <div className="flex items-center gap-1">
                                        <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-cert-${c.id}`}><Pencil size={14} /></button>
                                        {(c.computed_status === "expired" || (c.expiry_date && (new Date(c.expiry_date) - new Date()) / 86400000 <= 120)) && (
                                          <button onClick={(e) => { e.stopPropagation(); sendReminder(c); }} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Send reminder (available from 4 months before expiry)" data-testid={`remind-cert-${c.id}`}><Bell size={14} /></button>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); handleDelete(c); }} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-cert-${c.id}`}><Trash2 size={14} /></button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                {isOpen && hasAchievements && (
                                  <tr className="bg-slate-50/70">
                                    <td colSpan={isStaff ? 6 : 5} className="px-3 py-2">
                                      <div className="text-[11px] text-slate-500 mb-1 font-medium uppercase tracking-wide">
                                        Achievements on this certificate
                                      </div>
                                      <table className="w-full text-xs bg-white border rounded">
                                        <thead className="bg-slate-100 text-slate-600">
                                          <tr>
                                            <th className="px-2 py-1 text-left font-medium">Code</th>
                                            <th className="px-2 py-1 text-left font-medium">Category</th>
                                            <th className="px-2 py-1 text-left font-medium">Valid From</th>
                                            <th className="px-2 py-1 text-left font-medium">Expiry Date</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {c.achievements.map((a, i) => (
                                            <tr key={i} className="border-t">
                                              <td className="px-2 py-1 font-mono">{a.code || "—"}</td>
                                              <td className="px-2 py-1">{a.category || a.qualification_name || "—"}</td>
                                              <td className="px-2 py-1">{a.valid_from || "—"}</td>
                                              <td className="px-2 py-1">{a.expiry_date || "—"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                                </Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] flex flex-col" data-testid="cert-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Certificate" : "Add Certificates"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 overflow-y-auto flex-1 pr-1">
            {!editing && (
              <>
                <div>
                  <Label className="text-xs">Learner</Label>
                  <SearchableSelect
                    options={members.map(m => ({ value: m.id, label: m.name, hint: m.email || "(walk-in)" }))}
                    value={form.member_id}
                    onChange={(v) => setForm({ ...form, member_id: v })}
                    placeholder="Select learner"
                    searchPlaceholder="Search by name or email…"
                    testId="cert-member-select"
                  />
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <Label className="text-xs">Qualifications</Label>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-slate-500" data-testid="cert-quals-count">
                        {selectedQuals.length} selected
                      </span>
                      {selectedQuals.length > 0 && (
                        <button type="button" onClick={() => setSelectedQuals([])} className="text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="cert-clear-quals">
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <Input
                    value={qualSearch}
                    onChange={(e) => setQualSearch(e.target.value)}
                    placeholder="Filter qualifications…"
                    className="mt-1 h-8 text-xs"
                    data-testid="cert-qual-search"
                  />
                  <div className="mt-1 border border-border rounded-md max-h-56 overflow-y-auto bg-white" data-testid="cert-qual-list">
                    {quals
                      .filter(q => !qualSearch || (q.name || "").toLowerCase().includes(qualSearch.toLowerCase()) || (q.code || "").toLowerCase().includes(qualSearch.toLowerCase()))
                      .map(q => {
                        const checked = selectedQuals.includes(q.id);
                        return (
                          <label
                            key={q.id}
                            className={`flex items-center gap-2 px-2.5 py-1.5 text-xs border-b border-border/60 last:border-b-0 cursor-pointer hover:bg-slate-50 ${checked ? "bg-slate-50" : ""}`}
                            data-testid={`cert-qual-option-${q.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                setSelectedQuals(prev =>
                                  v ? Array.from(new Set([...prev, q.id])) : prev.filter(id => id !== q.id)
                                );
                              }}
                              data-testid={`cert-qual-check-${q.id}`}
                            />
                            <span className="flex-1 truncate">
                              <span className="font-medium">{q.code ? `${q.code} — ` : ""}{q.name}</span>
                              {q.certificate_validity_years ? <span className="text-slate-500 ml-1">({q.certificate_validity_years}yr)</span> : <span className="text-amber-600 ml-1">(no validity set)</span>}
                            </span>
                          </label>
                        );
                      })}
                    {quals.length === 0 && (
                      <div className="p-3 text-xs text-muted-foreground">Loading qualifications…</div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div><Label className="text-xs">Certificate Number {editing ? "" : <span className="text-slate-400 font-normal">(shared, optional)</span>}</Label><Input value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} data-testid="cert-number-input" /></div>
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => onIssueDateChange(e.target.value)} required data-testid="cert-issue-date" /></div>
                <div><Label className="text-xs">Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} required data-testid="cert-expiry-date" /></div>
              </div>
            ) : (
              <>
                <div><Label className="text-xs">Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm(f => ({ ...f, issue_date: e.target.value }))} required data-testid="cert-issue-date" /></div>
                {form.issue_date && selectedQuals.length > 0 && (
                  <div className="rounded-md border border-border bg-slate-50/50 p-2" data-testid="cert-expiry-preview">
                    <p className="text-[11px] font-medium text-slate-700 mb-1">Expiry preview</p>
                    <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                      {selectedQuals.map(qid => {
                        const q = quals.find(x => x.id === qid);
                        const exp = computeExpiry(form.issue_date, qid);
                        return (
                          <li key={qid} className="flex justify-between text-[11px]">
                            <span className="truncate mr-2">{q?.code ? `${q.code} — ` : ""}{q?.name || qid}</span>
                            <span className={exp ? "text-slate-600 tabular-nums" : "text-amber-600"}>{exp || "no validity"}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </>
            )}
            <div><Label className="text-xs">Certification Fee (£) {!editing && <span className="text-slate-400 font-normal">— per cert; defaults to each qualification&apos;s fee</span>}</Label><Input type="number" min="0" step="0.01" value={form.certification_fee} onChange={(e) => setForm({ ...form, certification_fee: parseFloat(e.target.value) || 0 })} data-testid="cert-fee-input" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cert-notes-input" /></div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Paperclip size={12} /> Certificate file (PDF, JPG, PNG; max 10 MB){!editing && selectedQuals.length > 1 && <span className="text-amber-600 ml-1">— attaches to first cert only</span>}</Label>
              {editing?.attachment_path && !attachment && (
                <div className="flex items-center gap-2 mt-1 p-2 border border-border rounded bg-slate-50/50">
                  <Paperclip size={12} className="text-muted-foreground shrink-0" />
                  <span className="text-xs truncate flex-1" data-testid="cert-current-attachment">{editing.attachment_filename || "attachment"}</span>
                  <button type="button" onClick={() => downloadAttachment(editing)} className="p-1 rounded hover:bg-slate-200 text-sky-600" title="Download"><Download size={12} /></button>
                  <button type="button" onClick={() => { removeAttachment(editing); setDialogOpen(false); }} className="p-1 rounded hover:bg-red-50 text-red-500" title="Remove"><X size={12} /></button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                className="text-xs mt-1 block w-full file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-xs file:font-medium hover:file:bg-slate-200"
                data-testid="cert-file-input"
              />
              {attachment && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Selected: <strong>{attachment.name}</strong> ({Math.round(attachment.size / 1024)} KB)
                </p>
              )}
            </div>
            {/* Hidden submit so Enter-key submits the form */}
            <button type="submit" aria-hidden="true" tabIndex={-1} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
          </form>
          <DialogFooter className="mt-3 pt-3 border-t border-border">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="button" onClick={handleSubmit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cert-submit-btn">
              {editing ? "Update" : `Create ${selectedQuals.length || ""} certificate${selectedQuals.length === 1 ? "" : "s"}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
